/**
 * The one-way link from "an entry was written" to "send it".
 *
 * ## Why a module and not a context
 *
 * `entryStore` is plain functions with no React in scope, and every writer in the
 * app already goes through it — the day sheet, the today screen, an import. A
 * context would only reach the writers that happen to be inside a component, and
 * the ones that are not are exactly where a missed push would go unnoticed.
 *
 * The alternative was to have `EntryContext` call the engine after each write,
 * which couples two providers and puts the correctness of sync in the hands of
 * whoever adds the next write path. Here a new writer gets sync for free.
 *
 * ## What must never be pushed
 *
 * Only LOCAL edits. `putMergedEntry` — the inbound half of sync — deliberately
 * does not notify. If it did, receiving a record would immediately send one back,
 * the far device would merge and send again, and two devices would keep each other
 * busy indefinitely over data neither had changed.
 *
 * Convergence does not need the echo. Each device merges the other's original
 * writes and, because the merge is deterministic, both arrive at the same result
 * without either having to broadcast its conclusion.
 */
import type { DayEntry } from '@domain/types'

const DEBUG_SYNC = String(import.meta.env?.VITE_SYNC_DEBUG ?? '') === '1'

function log(...args: any[]) {
  if (DEBUG_SYNC) {
    console.log(...args)
  }
}

function warn(...args: any[]) {
  if (DEBUG_SYNC) {
    console.warn(...args)
  }
}

function logError(...args: any[]) {
  if (DEBUG_SYNC) {
    console.error(...args)
  }
}

type Pusher = (entry: DayEntry) => Promise<void>

let pusher: Pusher | undefined

/**
 * Install or remove the sink. Passing `undefined` disconnects it.
 *
 * Owned by `SyncContext`, which sets it when sync is running and clears it when
 * consent is withdrawn, the app locks, or the provider unmounts. Clearing matters
 * as much as setting: a stale pusher would keep sending after permission ended.
 */
export function setEntryPusher(next: Pusher | undefined): void {
  pusher = next
}

/**
 * Called by `entryStore` after a local write lands.
 *
 * Deliberately fire-and-forget. A write to the local database has already
 * succeeded by this point and its success must not depend on a relay being
 * reachable — awaiting here would make logging a symptom wait on the network,
 * which is the exact coupling a local-first app exists to avoid.
 *
 * Failures are swallowed for the same reason. The engine tracks what is
 * unconfirmed and reports it as status; an exception thrown into a storage
 * function would surface as "could not save", which would be a lie.
 */
export function notifyEntryWritten(entry: DayEntry): void {
  log("[syncBridge] notifyEntryWritten called with date:", entry.date, "pusher defined:", !!pusher)
  if (!pusher) {
    warn("[syncBridge] Warning: pusher is undefined. Sync is not active or hasn't started yet.")
  }
  void pusher?.(entry).catch((e) => {
    logError("[syncBridge] pusher failed:", e)
  })
}
